import React, { useState, useEffect, useCallback, useContext } from 'react';
import apiClient from '../api';
import AuthContext from '../context/AuthContext';
import { formatDate } from '../utils/formatDate';
import ProbationForm from './ProbationForm';

function ProbationDetails({ juvenileId }) {
  const [offenses, setOffenses] = useState([]);
  const [probationRecords, setProbationRecords] = useState([]);
  const [error, setError] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const { hasPermission } = useContext(AuthContext);

  const fetchAllData = useCallback(async () => {
    if (!juvenileId) return;
    try {
      setError('');
      const [offensesRes, probationRes] = await Promise.all([
        apiClient.get(`/api/juveniles/${juvenileId}/offenses`),
        apiClient.get(`/api/case/juveniles/${juvenileId}/probation`)
      ]);
      // **THE FIX IS HERE:** Extract the array from the response object
      setOffenses(offensesRes.data.offenses || []);
      setProbationRecords(probationRes.data);
    } catch (err) {
      setError('Failed to fetch probation details.');
      console.error(err);
    }
  }, [juvenileId]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  const handleFormSubmit = async (formData) => {
    try {
      if (editingRecord) {
        await apiClient.put(`/api/case/probation/${editingRecord.id}`, formData);
      } else {
        await apiClient.post(`/api/case/offenses/${formData.offense_id}/probation`, formData);
      }
      setEditingRecord(null);
      setShowAddForm(false);
      fetchAllData();
    } catch (err) {
      console.error("Failed to save probation details", err);
    }
  };

  if (error) return <p className="text-red-500">{error}</p>;

  return (
    <div className="card">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold text-slate-800 mb-0">Probation Status</h2>
        {hasPermission('assign-probation') && !showAddForm && !editingRecord && (
          <button onClick={() => setShowAddForm(true)} className="bg-gold text-white hover:bg-gold-dark font-bold py-2 px-4 rounded-lg shadow-sm">
            Assign Probation
          </button>
        )}
      </div>

      {showAddForm && <ProbationForm offenses={offenses} onSubmit={handleFormSubmit} onCancel={() => setShowAddForm(false)} />}
      {editingRecord && <ProbationForm offenses={offenses} onSubmit={handleFormSubmit} initialData={editingRecord} onCancel={() => setEditingRecord(null)} />}

      <div className="mt-4 space-y-4">
        {probationRecords.length > 0 ? (
          probationRecords.map(record => (
            <div key={record.id} className="p-4 border rounded-lg bg-slate-50">
              <div className="flex justify-between items-start">
                <div>
                  <h4 className="font-bold text-slate-700">Offense: {record.offense_type} (Case: {record.case_number})</h4>
                  <p className={`text-sm font-semibold ${record.status === 'Active' ? 'text-blue-600' : 'text-slate-500'}`}>Status: {record.status}</p>
                </div>
                {hasPermission('assign-probation') && (
                <button onClick={() => setEditingRecord(record)} className="text-sm text-slate-600 hover:text-gold">Edit</button>
                )}
              </div>
              <div className="border-t mt-2 pt-2 text-sm space-y-1">
                <p><strong>Officer ID:</strong> {record.probation_officer_id || 'N/A'}</p>
                <p><strong>Start Date:</strong> {formatDate(record.start_date)} | <strong>End Date:</strong> {formatDate(record.end_date)}</p>
                <p><strong>Conditions:</strong> {record.conditions || 'N/A'}</p>
              </div>
            </div>
          ))
        ) : (
          !showAddForm && !editingRecord && <p>No probation records found.</p>
        )}
      </div>
    </div>
  );
}

export default ProbationDetails;